import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";

var _templateObject, _templateObject2, _templateObject3, _templateObject4;

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cuid from 'cuid';
import { calculateRem } from '@uie/javascript-style-helpers';
import RadioButtonLabel from './RadioButtonLabel';
import DefaultIcon from './RadioButtonIcon';
import { colors, calculateSpacing } from '@vds-core/theme';
import { BodyConfig, TypographyConfig } from '@vds-core/typography';
import { withVDSManager } from '@vds-core/utilities';
import { showError } from '../utils';
var propTypes = {
  /**
   * If provided, the Radio will be rendered with children.
   */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.node]),

  /**
   * If provided, the Radio will be rendered in the Disabled State.
   */
  disabled: PropTypes.bool,

  /**
   * The prop for the HTML name Attribute. This is used to specify a name for an input element. It is used to reference the form-data after submitting the form or to reference the element.
   */
  name: PropTypes.string.isRequired,

  /**
   * Callback function that returns the value of the radio when clicked.
   */
  onChange: PropTypes.func,

  /**
   * Value of RadioButton.
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * If provided, the Radio will be rendered in the Selected State.
   */
  selected: PropTypes.bool,

  /**
   * @ignore If passed, the Body component from VDS will render
   */
  Body: PropTypes.func,

  /**
   * @ignore
   */
  ariaLabel: PropTypes.string,

  /**
   * Boolean or Function that returns a boolean value that determines if component should be rendered in the Error State. Function receives the 'event' object.
   */
  error: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),

  /**
   *  Determines what event this component will check for an error, options are 'onBlur' or 'onChange'.
   */
  errorEvent: PropTypes.oneOf(['blur', 'change']),

  /**
   * @deprecated selected
   */
  checked: PropTypes.bool,

  /**
   * @ignore
   * If provided a string of a proper multiplier constant in calculate spacing, a bottom of a margin 4 times the input will render.
   */
  marginBottom: PropTypes.string,

  /**
   * @ignore
   * If provided, an object containing color values in hex will be returned
   */
  colorConfig: PropTypes.object,

  /**
   * @ignore
   * If provided, an object containing font-family, font-size, and line-height will be returned.
   */
  bodyConfig: PropTypes.object,

  /**
   * @ignore
   * If provided, a new RadioButtonIcon will be returned.
   */
  RadioButtonIcon: PropTypes.func,

  /**
   * @ignore
   * Typescale of text within RadioButton.
   */
  typescale: PropTypes.string,

  /**
   * @ignore
   * If provided, a hover state that thickens the border of the RadioButtonIcon will exist.
   */
  hoverState: PropTypes.bool,

  /**
   * @ignore
   * If provided, a dashed focus state will render RadioButtonIcon will exist.
   */
  focusState: PropTypes.bool,

  /**
   * @ignore
   * If provided, the RadioButton will break into a new line at the given string value width.
   */
  maxLabelWidth: PropTypes.string,

  /**
   * @ignore
   * If provided the RadioButton will be rendered in inverted color scheme.
   */
  inverted: PropTypes.bool,

  /**
   * @ignore
   * If provided, padding surrounding the RadioButton will render.
   */
  padding: PropTypes.string,

  /**
   * Allows a string to be provided for analytics.
   */
  'data-analyticstrack': PropTypes.string,

  /**
   * Allows a string to be provided for analytics.
   */
  'data-track': PropTypes.string,

  /**
   * Allows a string to be provided for click stream.
   */
  'data-clickstream': PropTypes.string,

  /**
   * Allows a unique ID to be passed to the component.
   */
  id: PropTypes.string,

  /**
   * Allows a unique ID to be passed to the input element.
   */
  inputId: PropTypes.string
};
var defaultProps = {
  value: '',
  selected: undefined,
  children: '',
  disabled: false,
  error: undefined,
  errorEvent: 'blur',
  onChange: function onChange() {},
  className: null,
  marginBottom: null,
  bodyConfig: BodyConfig,
  colorConfig: colors,
  name: '',
  RadioButtonIcon: DefaultIcon,
  typescale: TypographyConfig.getTypescale(),
  hoverState: false,
  focusState: false,
  maxLabelWidth: undefined,
  inverted: false,
  padding: calculateRem(2, 0, 24, 8)
};
var ComponentWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ComponentWrapper",
  componentId: "sc-bccgul-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  box-sizing: border-box;\n  align-items: flex-start;\n  cursor: pointer;\n  display: inline-flex;\n  position: relative;\n  ", ";\n  padding: ", ";\n\n  ", ";\n"])), function (_ref) {
  var marginBottom = _ref.marginBottom;
  return marginBottom ? "\n      margin: 0 0 ".concat(calculateSpacing(marginBottom, 'rem'), " 0;\n      &:last-child {\n        margin: 0;\n      }") : "\n      margin: 0 0 0 ".concat(calculateSpacing('5X', 'rem'), ";\n  ");
}, function (_ref2) {
  var padding = _ref2.padding;
  return padding;
}, function (_ref3) {
  var hoverState = _ref3.hoverState,
      error = _ref3.error,
      disabled = _ref3.disabled,
      colorConfig = _ref3.colorConfig,
      inverted = _ref3.inverted;
  return hoverState && !error && !disabled && "\n    &:hover .radioOuter::after {\n      border: none !important;\n    }\n\n    &:hover .radioOuter {\n      outline: none;\n      box-shadow: 0 0 0 ".concat(calculateRem(1), " ").concat(inverted ? colorConfig.white : colorConfig.black, ";\n    }\n    ");
});
var StyledInput = /*#__PURE__*/styled.input.withConfig({
  displayName: "StyledInput",
  componentId: "sc-bccgul-1"
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  clip: rect(0 0 0 0);\n  position: absolute;\n  opacity: 0;\n  overflow: hidden;\n  padding: 0;\n  border: 0;\n  white-space: nowrap;\n  ", ";\n  ", ";\n"])), function (_ref4) {
  var error = _ref4.error,
      colorConfig = _ref4.colorConfig,
      focusState = _ref4.focusState,
      showFocus = _ref4.showFocus;
  return !focusState && showFocus && "\n    &:focus + label > svg {\n      border-radius: 50%;\n      box-shadow: ".concat(error ? "".concat(calculateRem(0, 0, 0, 1), " ").concat(colorConfig.orange) : "".concat(calculateRem(0, 0, 0, 1), " ").concat(colorConfig.black), ";\n      outline-offset: ").concat(calculateRem(-1), ";\n    }\n  ");
}, function (_ref5) {
  var focusState = _ref5.focusState,
      colorConfig = _ref5.colorConfig,
      showFocus = _ref5.showFocus,
      inverted = _ref5.inverted;
  return focusState && showFocus && "\n      &:focus:not(:hover) + label {\n        .radioOuter {\n          outline: none;\n          &::after {\n            display: inline-block;\n            border: ".concat(calculateRem(1), " dashed ").concat(inverted ? colorConfig.white : colorConfig.black, ";\n            border-radius: 50%;\n            content: '';\n            height: calc(100% + ").concat(calculateRem(6), ");\n            left: 50%; \n            position: absolute;\n            top: 50%;\n            transform: translate(-50%, -50%);\n            width: calc(100% + ").concat(calculateRem(6), ");\n          }\n        }\n      };\n  ");
});
var ChildWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ChildWrapper",
  componentId: "sc-bccgul-2"
})(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  position: relative;\n  text-align: left;\n  > * {\n    display: block;\n    margin: 0px !important;\n    + a {\n      margin-top: 4px !important;\n    }\n  }\n"])));
var LabelWrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "LabelWrapper",
  componentId: "sc-bccgul-3"
})(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  display: flex;\n  flex: 1;\n  padding-bottom: ", ";\n"])), calculateSpacing('1X'));

var RadioButton = /*#__PURE__*/function (_React$Component) {
  _inherits(RadioButton, _React$Component);

  var _super = _createSuper(RadioButton);

  function RadioButton(props) {
    var _this2;

    _classCallCheck(this, RadioButton);

    _this2 = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this2), "componentDidMount", function () {
      _this2.isControlled = _this2.props.checked !== undefined || _this2.props.selected !== undefined;
    });

    _defineProperty(_assertThisInitialized(_this2), "componentDidUpdate", function (prevProps, prevState) {
      if (prevProps && prevProps.selected !== _this2.props.selected) {
        _this2.setState(function (prevProps) {
          return {
            selected: !prevProps.selected
          };
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_checkBrowser", function () {
      var ua = window.navigator.userAgent;
      var IE10orOlder = ua.indexOf('MSIE ') > 0;
      var IE11 = ua.indexOf('Trident/') > 0;
      var edge = ua.indexOf('Edge/') > 0;
      var chrome = ua.indexOf('Chrome') > 0;
      var safari = ua.indexOf('Safari') > 0;
      var opera = ua.indexOf('OP') > 0;
      var firefox = ua.indexOf('Firefox') > 0;
      if (IE10orOlder || IE11) return 'IE';
      if (edge) return 'Edge';
      if (safari && !chrome) return 'Safari';
      if (opera) return 'Opera';
      if (firefox) return 'Firefox';
      if (chrome) return 'Chrome';
      return null;
    });

    _defineProperty(_assertThisInitialized(_this2), "_onChange", function (e) {
      var hovered = _this2.state.hovered;
      if (hovered) _this2.setState({
        showFocus: false
      });else _this2.setState({
        showFocus: true
      });

      if (_this2.isControlled) {
        _this2.props.onChange(e);
      } else {
        _this2.setState(function (prevState) {
          return {
            selected: !prevState.selected
          };
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_blurOnClick", function (e) {
      // const { browser } = this.state;
      // if (browser === 'IE' && e.button === 0) {
      //   e.currentTarget.blur();
      // } else if (
      //   browser !== 'IE' &&
      //   browser !== 'Safari' &&
      //   e.type === 'click' &&
      //   e.clientX !== 0 &&
      //   e.clientY !== 0
      // ) {
      //   e.currentTarget.blur();
      //   // check for error on select
      // }
      if (_this2.props.errorEvent === 'change') {
        var _this = _assertThisInitialized(_this2);

        showError(e, _this);
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_onBlur", function (e) {
      if (_this2.props.errorEvent === 'blur') {
        var _this = _assertThisInitialized(_this2);

        showError(e, _this);
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "isAppleDevice", function () {
      return /iPhone|iPad/i.test(navigator.userAgent);
    }());

    _defineProperty(_assertThisInitialized(_this2), "_addHover", function (e) {
      if (!_this2.state.hovered) _this2.setState({
        hovered: true
      });
    });

    _defineProperty(_assertThisInitialized(_this2), "_removeHover", function (e) {
      return _this2.setState({
        hovered: false
      });
    });

    _defineProperty(_assertThisInitialized(_this2), "_onFocus", function (e) {
      if (!_this2.state.hovered) _this2.setState({
        showFocus: true
      });else _this2.setState({
        showFocus: false
      });
    });

    _this2.state = {
      selected: _this2.props.selected || _this2.props.checked || false,
      browser: _this2._checkBrowser(),
      showFocus: true
    };
    return _this2;
  }

  _createClass(RadioButton, [{
    key: "render",
    value: function render() {
      this.id = cuid();
      var _this$props = this.props,
          checkedProp = _this$props.checked,
          selectedProp = _this$props.selected,
          children = _this$props.children,
          label = _this$props.label,
          Body = _this$props.Body,
          className = _this$props.className,
          disabled = _this$props.disabled,
          errorProp = _this$props.error,
          name = _this$props.name,
          value = _this$props.value,
          ariaLabel = _this$props.ariaLabel,
          colorConfig = _this$props.colorConfig,
          bodyConfig = _this$props.bodyConfig,
          marginBottom = _this$props.marginBottom,
          RadioButtonIcon = _this$props.RadioButtonIcon,
          typescale = _this$props.typescale,
          hoverState = _this$props.hoverState,
          focusState = _this$props.focusState,
          maxLabelWidth = _this$props.maxLabelWidth,
          inverted = _this$props.inverted,
          padding = _this$props.padding,
          id = _this$props.id,
          inputId = _this$props.inputId,
          analyticsTrack = _this$props['data-analyticstrack'],
          track = _this$props['data-track'],
          clickStream = _this$props['data-clickstream'];
      var _this$state = this.state,
          selected = _this$state.selected,
          errorState = _this$state.error; // Error state has preference over component's error
      // Because errorProp might be a function, we first need to check if prop is a bool and only then assign its value

      var error = errorState || typeof errorProp === 'boolean' && errorProp;
      var radioId = inputId ? inputId : cuid();
      var color = inverted ? colorConfig.white : colorConfig.black;
      var disabledColor = inverted ? colorConfig.coolGray10 : colorConfig.coolGray3;
      var childWrapperProps = {};
      /*Condition to add role attribute in childWrapper*/

      if (this.isAppleDevice && ariaLabel) childWrapperProps.role = 'text';
      /* Input can not be nested inside of label, as JAWS will not correctly read the number of items. Also Input is before Label for css selector for focus state*/

      return /*#__PURE__*/React.createElement(ComponentWrapper, {
        marginBottom: marginBottom,
        hoverState: hoverState,
        padding: padding,
        inverted: inverted,
        disabled: disabled,
        error: error,
        colorConfig: colorConfig,
        onMouseEnter: this._addHover,
        onMouseLeave: this._removeHover
      }, /*#__PURE__*/React.createElement(StyledInput, {
        colorConfig: colorConfig,
        focusState: focusState,
        checked: selected,
        disabled: disabled,
        id: radioId,
        name: name,
        onBlur: this._onBlur,
        onChange: this._onChange,
        onClick: this._blurOnClick,
        role: "radio",
        type: "radio",
        value: value,
        showFocus: this.state.showFocus,
        onFocus: this._onFocus,
        inverted: inverted
      }), /*#__PURE__*/React.createElement(RadioButtonLabel, {
        id: id,
        className: className,
        name: name,
        bodyConfig: bodyConfig,
        colorConfig: colorConfig,
        disabled: disabled,
        selected: selected,
        typescale: typescale,
        maxLabelWidth: maxLabelWidth,
        htmlFor: radioId,
        "aria-label": ariaLabel,
        "data-track": track,
        "data-analyticstrack": analyticsTrack,
        "data-clickstream": clickStream,
        inverted: inverted
      }, /*#__PURE__*/React.createElement(RadioButtonIcon, {
        selected: selected,
        disabled: disabled,
        error: error,
        colorConfig: colorConfig,
        inverted: inverted
      }), /*#__PURE__*/React.createElement(ChildWrapper, childWrapperProps, label && Body && /*#__PURE__*/React.createElement(LabelWrapper, null, /*#__PURE__*/React.createElement(Body, {
        size: "large",
        bold: true,
        color: disabled ? disabledColor : color
      }, label)), children)));
    }
  }]);

  return RadioButton;
}(React.Component);

RadioButton.propTypes = propTypes;
RadioButton.defaultProps = defaultProps;
export default withVDSManager(RadioButton);